"use client"

import { useState, useEffect } from "react"
import { Trophy, Sword, Skull, TrendingUp, Shield, ChevronLeft, ChevronRight } from "lucide-react"

const TABS = [
    { key: "pvp", label: "PvP", icon: Sword, color: "text-red-500" },
    { key: "pk", label: "PK", icon: Skull, color: "text-orange-500" },
    { key: "level", label: "Level", icon: TrendingUp, color: "text-blue-500" },
    { key: "clan", label: "Clans", icon: Shield, color: "text-amber-500" },
] as const

type RankType = (typeof TABS)[number]["key"]

interface CharRank {
    char_name: string
    level: number | null
    classid: number | null
    pvpkills: number | null
    pkkills: number | null
    online: number | null
    clan_name: string | null
}

interface ClanRank {
    clan_name: string | null
    clan_level: number | null
    reputation_score: number
    ally_name: string | null
    leader_name: string
    members: number
}

// Common L2 class names (3rd class)
const CLASS_NAMES: Record<number, string> = {
    88: "Duelist", 89: "Dreadnought", 90: "Phoenix Knight", 91: "Hell Knight",
    92: "Sagittarius", 93: "Adventurer", 94: "Archmage", 95: "Soultaker",
    96: "Arcana Lord", 97: "Cardinal", 98: "Hierophant", 99: "Eva Templar",
    100: "Sword Muse", 101: "Wind Rider", 102: "Moonlight Sentinel",
    103: "Mystic Muse", 104: "Elemental Master", 105: "Eva Saint",
    106: "Shillien Templar", 107: "Spectral Dancer", 108: "Ghost Hunter",
    109: "Ghost Sentinel", 110: "Storm Screamer", 111: "Spectral Master",
    112: "Shillien Saint", 113: "Titan", 114: "Grand Khavatari",
    115: "Dominator", 116: "Doomcryer", 117: "Fortune Seeker", 118: "Maestro",
    127: "Berserker", 131: "Doombringer", 132: "Soulhound", 133: "Soulhound",
    134: "Trickster", 135: "Inspector", 136: "Judicator",
}

function getClassName(id: number | null): string {
    if (id == null) return "Unknown"
    return CLASS_NAMES[id] || `Class ${id}`
}

export default function RankingPage() {
    const [tab, setTab] = useState<RankType>("pvp")
    const [data, setData] = useState<CharRank[] | ClanRank[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)

    useEffect(() => {
        setPage(1)
    }, [tab])

    useEffect(() => {
        const fetchRanking = async () => {
            setLoading(true)
            try {
                const res = await fetch(`/api/ranking?type=${tab}&limit=25&page=${page}`)
                if (res.ok) {
                    const json = await res.json()
                    setData(json.rankings || [])
                    setTotalPages(json.pages || 1)
                    setTotal(json.total || 0)
                }
            } catch {
                console.error("Failed to fetch rankings")
            } finally {
                setLoading(false)
            }
        }
        fetchRanking()
    }, [tab, page])

    const activeTab = TABS.find((t) => t.key === tab)!

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="container mx-auto px-4 py-16">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-500 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <Trophy className="w-3 h-3" />
                        Leaderboards
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter text-white mb-4 uppercase">
                        Top <span className="text-amber-500">Players</span>
                    </h1>
                    <p className="text-slate-400 text-lg max-w-md mx-auto italic">
                        The most powerful warriors and clans of DragonL2.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {TABS.map((t) => {
                        const Icon = t.icon
                        const active = tab === t.key
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold uppercase tracking-widest transition-all ${active
                                    ? "bg-slate-800 text-white border border-slate-700"
                                    : "text-slate-500 hover:text-slate-300 border border-transparent hover:border-slate-800"
                                    }`}
                            >
                                <Icon className={`w-4 h-4 ${active ? t.color : ""}`} />
                                {t.label}
                            </button>
                        )
                    })}
                </div>

                {/* Table */}
                <div className="rounded-2xl border border-slate-800/50 bg-slate-900/40 overflow-hidden">
                    {loading ? (
                        <div className="p-8 space-y-3">
                            {[...Array(10)].map((_, i) => (
                                <div key={i} className="h-12 bg-slate-800/30 rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : data.length === 0 ? (
                        <div className="text-center py-20">
                            <activeTab.icon className={`w-16 h-16 ${activeTab.color} mx-auto mb-4 opacity-30`} />
                            <p className="text-slate-500 text-lg">No data available yet.</p>
                        </div>
                    ) : tab === "clan" ? (
                        <ClanTable data={data as ClanRank[]} page={page} />
                    ) : (
                        <CharacterTable data={data as CharRank[]} tab={tab} page={page} />
                    )}
                </div>

                {/* Footer info + Pagination */}
                <div className="flex items-center justify-between mt-6">
                    <p className="text-slate-600 text-sm">
                        {total > 0 && `${total.toLocaleString()} total entries`}
                    </p>
                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-slate-400 text-sm font-bold px-3">
                                {page} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function CharacterTable({ data, tab, page }: { data: CharRank[]; tab: RankType; page: number }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-slate-800/50 text-slate-500 text-xs font-bold uppercase tracking-widest">
                        <th className="px-6 py-4 text-left w-16">#</th>
                        <th className="px-6 py-4 text-left">Name</th>
                        <th className="px-6 py-4 text-left">Class</th>
                        <th className="px-6 py-4 text-center">Level</th>
                        <th className="px-6 py-4 text-left">Clan</th>
                        <th className="px-6 py-4 text-right">
                            {tab === "pk" ? "PK Kills" : "PvP Kills"}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((player, idx) => {
                        const rank = (page - 1) * 25 + idx + 1
                        const isTop3 = rank <= 3
                        return (
                            <tr
                                key={player.char_name}
                                className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors"
                            >
                                <td className="px-6 py-4">
                                    {isTop3 ? (
                                        <span
                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${rank === 1
                                                ? "bg-amber-500/20 text-amber-400"
                                                : rank === 2
                                                    ? "bg-slate-400/20 text-slate-300"
                                                    : "bg-orange-500/20 text-orange-400"
                                                }`}
                                        >
                                            {rank}
                                        </span>
                                    ) : (
                                        <span className="text-slate-600 font-bold">{rank}</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 font-bold text-white">{player.char_name}</td>
                                <td className="px-6 py-4 text-slate-400">{getClassName(player.classid)}</td>
                                <td className="px-6 py-4 text-center text-slate-300 font-semibold">{player.level}</td>
                                <td className="px-6 py-4 text-slate-500">{player.clan_name || "—"}</td>
                                <td className="px-6 py-4 text-right font-black tabular-nums">
                                    <span className={tab === "pk" ? "text-orange-400" : "text-red-400"}>
                                        {(tab === "pk" ? player.pkkills : player.pvpkills)?.toLocaleString() || 0}
                                    </span>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function ClanTable({ data, page }: { data: ClanRank[]; page: number }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-slate-800/50 text-slate-500 text-xs font-bold uppercase tracking-widest">
                        <th className="px-6 py-4 text-left w-16">#</th>
                        <th className="px-6 py-4 text-left">Clan</th>
                        <th className="px-6 py-4 text-center">Level</th>
                        <th className="px-6 py-4 text-left">Leader</th>
                        <th className="px-6 py-4 text-left">Alliance</th>
                        <th className="px-6 py-4 text-center">Members</th>
                        <th className="px-6 py-4 text-right">Reputation</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((clan, idx) => {
                        const rank = (page - 1) * 25 + idx + 1
                        const isTop3 = rank <= 3
                        return (
                            <tr
                                key={`${clan.clan_name}-${idx}`}
                                className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors"
                            >
                                <td className="px-6 py-4">
                                    {isTop3 ? (
                                        <span
                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${rank === 1
                                                ? "bg-amber-500/20 text-amber-400"
                                                : rank === 2
                                                    ? "bg-slate-400/20 text-slate-300"
                                                    : "bg-orange-500/20 text-orange-400"
                                                }`}
                                        >
                                            {rank}
                                        </span>
                                    ) : (
                                        <span className="text-slate-600 font-bold">{rank}</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 font-bold text-white">{clan.clan_name}</td>
                                <td className="px-6 py-4 text-center text-amber-400 font-bold">{clan.clan_level}</td>
                                <td className="px-6 py-4 text-slate-300">{clan.leader_name}</td>
                                <td className="px-6 py-4 text-slate-500">{clan.ally_name || "—"}</td>
                                <td className="px-6 py-4 text-center text-slate-300">{clan.members}</td>
                                <td className="px-6 py-4 text-right font-black tabular-nums text-amber-400">
                                    {(clan.reputation_score ?? 0).toLocaleString()}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
